import { X } from "lucide-react";
import { type ReactNode, useEffect } from "react";
import { Button, cn } from "../../ui/index.js";

/** Right-side slide-over for the secondary surfaces (Lineup, Statistics,
 *  Setup, Shortcuts). Closes on Esc or backdrop click. Mounted only when
 *  open so the heavy editors don't run while hidden. */
type Props = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  width?: string;
  children: ReactNode;
};

export function Drawer({ open, onClose, title, width = "max-w-2xl", children }: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* biome-ignore lint/a11y/noStaticElementInteractions: backdrop dismiss */}
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: Esc handled above */}
      <div className="absolute inset-0 bg-canvas/70 backdrop-blur-sm" onClick={onClose} />
      <div
        className={cn(
          "relative flex h-full w-full flex-col border-l border-border-strong bg-surface shadow-[0_0_60px_-12px_var(--p-shadow-xl)]",
          width,
        )}
      >
        <header className="flex items-center justify-between border-b border-divider px-5 py-3.5">
          <h2 className="text-[15px] font-semibold tracking-[-0.01em] text-ink">{title}</h2>
          <Button
            variant="secondary"
            size="md"
            iconOnly
            aria-label="Yopish"
            onClick={onClose}
            className="text-ink-muted"
          >
            <X className="h-4 w-4" />
          </Button>
        </header>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
}
